import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings } from 'lucide-react';
import { Button } from './ui/button';

export const CookieBanner = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  const isPt = language === 'pt';

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString(),
    }));
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, functional: true, analytics: true });
  };

  const handleAcceptNecessary = () => {
    saveConsent({ necessary: true, functional: false, analytics: false });
  };

  const handleSavePreferences = () => {
    saveConsent({ necessary: true, functional, analytics });
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <div data-testid="cookie-banner" className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="container mx-auto max-w-4xl">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {!showSettings ? (
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary-deep/10 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-primary-deep" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-bold text-foreground-dark mb-2">
                    {isPt ? 'Utilizamos cookies' : 'We use cookies'}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    {isPt 
                      ? 'Utilizamos cookies para melhorar a sua experiência no nosso website.'
                      : 'We use cookies to improve your experience on our website.'}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      data-testid="cookie-accept-all"
                      className="h-10 px-6 rounded-full bg-primary-deep text-white hover:bg-accent-blue transition-colors"
                    >
                      {isPt ? 'Aceitar todos' : 'Accept all'}
                    </Button>
                    <Button
                      onClick={handleAcceptNecessary}
                      data-testid="cookie-accept-necessary"
                      variant="outline"
                      className="h-10 px-6 rounded-full border-slate-300 text-slate-700 hover:bg-slate-100"
                    >
                      {isPt ? 'Apenas essenciais' : 'Essential only'}
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      data-testid="cookie-customize"
                      variant="ghost"
                      className="h-10 px-6 rounded-full text-primary-deep hover:bg-primary-deep/10"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {isPt ? 'Personalizar' : 'Customize'}
                    </Button>
                  </div>
                  <div className="mt-4">
                    <Link to="/cookies" className="text-sm text-primary-deep hover:text-accent-blue underline">
                      {isPt ? 'Mais informações' : 'More information'}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-bold text-foreground-dark">
                  {isPt ? 'Personalizar' : 'Customize'}
                </h3>
                <button onClick={() => setShowSettings(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h4 className="font-semibold text-foreground-dark text-sm">
                      {isPt ? 'Cookies essenciais' : 'Essential cookies'}
                    </h4>
                    <p className="text-slate-500 text-xs mt-1">
                      {isPt ? 'Necessários. Não podem ser desativados.' : 'Required. Cannot be disabled.'}
                    </p>
                  </div>
                  <div className="w-12 h-6 bg-primary-deep rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h4 className="font-semibold text-foreground-dark text-sm">
                      {isPt ? 'Cookies funcionais' : 'Functional cookies'}
                    </h4>
                    <p className="text-slate-500 text-xs mt-1">
                      {isPt ? 'Funcionalidades melhoradas.' : 'Enhanced features.'}
                    </p>
                  </div>
                  <button
                    onClick={() => setFunctional(!functional)}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      functional ? 'bg-primary-deep justify-end' : 'bg-slate-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h4 className="font-semibold text-foreground-dark text-sm">
                      {isPt ? 'Cookies analíticos' : 'Analytical cookies'}
                    </h4>
                    <p className="text-slate-500 text-xs mt-1">
                      {isPt ? 'Análise de utilização.' : 'Usage analytics.'}
                    </p>
                  </div>
                  <button
                    onClick={() => setAnalytics(!analytics)}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      analytics ? 'bg-primary-deep justify-end' : 'bg-slate-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </button>
                </div>
              </div>

              <Button
                onClick={handleSavePreferences}
                data-testid="cookie-save-preferences"
                className="h-10 px-6 rounded-full bg-primary-deep text-white hover:bg-accent-blue transition-colors"
              >
                {isPt ? 'Guardar preferências' : 'Save preferences'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
